import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, FilterList as FilterListIcon, Clear as ClearIcon } from '@mui/icons-material';
import { BranchCreatePopup } from '../../components/common/BranchCreatePopup';
import { BranchCard } from '../../components/common/BranchCard';
import { BranchDetailModal } from '../../components/common/BranchDetailModal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { branchService } from '../../services/branchService';
import toast from 'react-hot-toast';

export const BranchList = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const itemsPerPage = 9;

  const loadBranches = async () => {
    setLoading(true);
    try {
      const response = await branchService.getBranches();
      if (response.data && response.data.data) {
        setBranches(response.data.data);
      } else {
        setBranches([]);
      }
    } catch (err) {
      console.error('Failed to load branches:', err);
      const errorMessage = 
        err.response?.data?.message || 
        err.response?.statusText || 
        err.message ||
        'Failed to load branches';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreatePopup = async () => {
    // Request geolocation first
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => setPopupOpen(true),
        () => setPopupOpen(true), // Open popup even if location is denied
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      setPopupOpen(true); // Open popup if geolocation not supported
    }
  };

  const handleEditClick = (branch) => {
    setEditingBranch(branch);
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setEditingBranch(null);
  };

  const handlePopupSuccess = () => {
    setPopupOpen(false);
    setEditingBranch(null);
    loadBranches(); // Refresh the list
  };

  const handleDeleteClick = (branch) => {
    setSelectedBranch(branch);
    setDetailModalOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (branch) => {
    try {
      const response = await branchService.toggleBranchStatus(branch.id);
      if (response.data && response.data.data) {
        // Update the branch in the local state
        setBranches((prev) =>
          prev.map((b) =>
            b.id === branch.id ? { ...b, is_active: response.data.data.is_active } : b
          )
        );
        toast.success(`Branch ${response.data.data.is_active ? 'activated' : 'deactivated'} successfully`);
      }
    } catch (err) {
      console.error('Branch toggle status error', err);
      const message =
        err.response?.data?.message ||
        err.response?.statusText ||
        err.message ||
        'Failed to toggle branch status';
      toast.error(message);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBranch) return;

    setDeleting(true);

    try {
      await branchService.deleteBranch(selectedBranch.id);
      setBranches((prev) => prev.filter((branch) => branch.id !== selectedBranch.id));
      setDeleteDialogOpen(false);
      setSelectedBranch(null);
      toast.success('Branch deleted successfully');
    } catch (err) {
      console.error('Branch delete error', err);
      if (err.response?.status === 404) {
        toast.error('Branch not found or already deleted.');
      } else {
        const dependencyError = err.response?.data?.errors?.dependencies;
        const message =
          dependencyError ||
          err.response?.data?.message ||
          err.response?.data ||
          err.message ||
          'Failed to delete branch';
        toast.error(message);
      }
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    console.log('BranchList component mounted, loading branches...');
    loadBranches();
  }, []);

  const filteredBranches = useMemo(() => {
    return branches.filter((branch) => {
      const lowerSearch = searchTerm.trim().toLowerCase();
      const bySearch =
        !lowerSearch ||
        branch.name?.toLowerCase().includes(lowerSearch) ||
        branch.city?.toLowerCase().includes(lowerSearch) ||
        branch.state?.toLowerCase().includes(lowerSearch) ||
        branch.phone?.toLowerCase().includes(lowerSearch);

      const byStatus =
        statusFilter === 'all' ? true : statusFilter === 'active' ? branch.is_active : !branch.is_active;

      return bySearch && byStatus;
    });
  }, [branches, searchTerm, statusFilter]);

  const paginationCount = Math.ceil(filteredBranches.length / itemsPerPage) || 1;
  const paginatedBranches = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredBranches.slice(start, start + itemsPerPage);
  }, [filteredBranches, page, itemsPerPage]);

  const handleViewBranch = (branch) => {
    setSelectedBranch(branch);
    setDetailModalOpen(true);
  };

  const handleEditFromModal = (branch) => {
    setDetailModalOpen(false);
    handleEditClick(branch);
  };

  const BranchDetail = ({ branch }) => {
    if (!branch) {
      return (
        <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Branch details
          </Typography>
          <Typography color="text.secondary">Click a branch card to view details in a modal.</Typography>
        </Card>
      );
    }

    return null;
  };

  return (
    <div style={{ display: 'contents' }}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: '100%', width: '100%', bgcolor: '#f7f9fc' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header Section */}
        <Paper elevation={1} sx={{ mb: 3, p: 3, borderRadius: 3, border: '1px solid #dde4eb', bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Branches
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your branch locations and operations
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                  {filteredBranches.length} branch{filteredBranches.length !== 1 ? 'es' : ''} found
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreatePopup}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  px: 2.2,
                  py: 1,
                }}
              >
                Add Branch
              </Button>
            </Box>
          </Paper>

          <BranchCreatePopup
            open={popupOpen}
            onClose={handleClosePopup}
            onSuccess={handlePopupSuccess}
            editingBranch={editingBranch}
          />

          {/* Search & Filter Bar */}
          <Paper elevation={0} sx={{ mb: 3, p: { xs: 1.2, sm: 1.5 }, borderRadius: 2, border: '1px solid #d8e2ee', bgcolor: '#f3f6fb' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto auto' }, gap: 1.25, alignItems: 'center' }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search branches..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
                sx={{ width: '100%', bgcolor: '#fff', borderRadius: 1 }}
              />
              <FormControl size="small" sx={{ width: '100%' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  startAdornment={<FilterListIcon sx={{ mr: 1 }} color="action" />}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                sx={{ width: '100%', textTransform: 'none' }}
              >
                Clear
              </Button>
            </Box>
          </Paper>

        {/* Content Area */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredBranches.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', bgcolor: '#f1f5fb', border: '1px dashed #cfdaf0' }}>
            <Typography color="textSecondary" variant="h6" sx={{ mb: 1 }}>
              No branches match your search/filter criteria
            </Typography>
            <Typography color="textSecondary" variant="body2">
              Try expanding your search or reset filters.
            </Typography>
          </Card>
        ) : (
          <>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{ mb: 4 }}>
              {paginatedBranches.map((branch) => (
                <Grid item xs={12} sm={6} md={4} key={branch.id}>
                  <BranchCard
                    branch={branch}
                    selected={selectedBranch?.id === branch.id}
                    onView={handleViewBranch}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    onToggleStatus={handleToggleStatus}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={paginationCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
              />
            </Box>
          </>
        )}
      </Box>
    </Box>

    <BranchDetailModal
      open={detailModalOpen}
      branch={selectedBranch}
      onClose={() => setDetailModalOpen(false)}
      onEdit={handleEditFromModal}
      onDelete={handleDeleteClick}
      onToggleStatus={handleToggleStatus}
    />

    <ConfirmModal
      open={deleteDialogOpen}
      title="Delete Branch"
      message={`Are you sure you want to delete ${selectedBranch?.name || 'this branch'}? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      loading={deleting}
      onCancel={() => setDeleteDialogOpen(false)}
      onConfirm={handleDeleteConfirm}
    />
    </div>
  );
};
